import React, { FC, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import { EnvironmentsIcon, PostProcessIcon } from '../../../../../widgets/icons/scene';
import { thinScrollbar } from '../../../../../widgets/scrollbar';
import WorldEditor from './worldEditor';
import EffectsEditor from './effectsEditor';

// LOOK: CIÒ CHE STA INTORNO ALLA SCENA (round 4, prototipo #26). World ed Effects erano due voci del
// rail; ora sono due righe di un elenco, e ogni riga apre il suo pannello nell'Inspector come una
// riga di Objects apre quello del suo oggetto. Shadows e Render del prototipo non ci sono: il primo
// aspetta il Ground (round 3 § 9), il secondo non ha niente dietro (registro, riga 30).

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
`;

// Alta quanto la barra dei comandi delle altre sezioni: passando da Objects a Look l'elenco comincia
// alla stessa altezza.
const Header = styled.div`
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 6px 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-height: 0;
    padding: 10px;
    overflow-y: auto;
    ${thinScrollbar}
`;

// La riga di un elenco della scena: stesse misure e stessa selezione delle righe di `TreeComponent`.
const Row = styled.button<{ $selected: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 34px;
    padding: 0 6px;
    border: 0;
    border-radius: 8px;
    background: ${(props) => (props.$selected ? `${props.theme.app.secondary}14` : 'transparent')};
    color: ${(props) => props.theme.app.textColor};
    font-family: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;

    &::before {
        position: absolute;
        top: 6px;
        bottom: 6px;
        left: 0;
        display: ${(props) => (props.$selected ? 'block' : 'none')};
        width: 2.5px;
        background: ${(props) => props.theme.app.secondary};
        border-radius: 2px;
        content: '';
    }

    &:hover {
        background: ${(props) => props.theme.app.backgroundToolbar};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: -2px;
    }

    svg {
        flex: none;
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const RowName = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// «Si apre di là»: la freccia dice che la riga porta all'Inspector, non che si espande qui.
const RowGo = styled.span`
    display: inline-flex;
    color: ${(props) => props.theme.app.primary};
`;

type LookView = 'environment' | 'postprocess';

interface LookEditorProps {
    sceneEditor?: any;
    zkScene?: any;
    additionalFeatures?: any;
    onSceneChanged?: () => void;
    onInspectorChange?: (content: ReactNode | null) => void;
    /** Environment regola fondale e ambiente: aprirlo accende il fondale (utente, 2026-09-22). */
    onEnvironmentOpen?: () => void;
}

const LookEditor: FC<LookEditorProps> = ({ sceneEditor, zkScene, additionalFeatures, onSceneChanged, onInspectorChange, onEnvironmentOpen }) => {
    const [view, setView] = useState<LookView>('environment');

    const rows: { key: LookView; label: string; icon: ReactNode; panel: () => ReactNode }[] = [
        {
            key: 'environment',
            label: T._('Environment', 'Admin'),
            icon: <EnvironmentsIcon />,
            panel: () => <WorldEditor sceneEditor={sceneEditor} zkScene={zkScene} additionalFeatures={additionalFeatures} onSceneChanged={onSceneChanged} />
        },
        {
            key: 'postprocess',
            label: T._('Post-process', 'Admin'),
            icon: <PostProcessIcon />,
            panel: () => <EffectsEditor sceneEditor={sceneEditor} zkScene={zkScene} />
        }
    ];

    const open = (key: LookView) => {
        setView(key);
        onInspectorChange?.(rows.find((row) => row.key === key)?.panel());
        if (key === 'environment') onEnvironmentOpen?.();
    };

    // Entrare in Look apre la prima riga, come nel prototipo: una sezione che mostra due nomi e un
    // Inspector vuoto chiederebbe un clic in più per arrivare alla sola cosa che c'è da fare.
    useEffect(() => {
        open('environment');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Panel aria-label={T._('Look', 'Admin')}>
            <Header>{T._('Look', 'Admin')}</Header>
            <List role='listbox' aria-label={T._('Look', 'Admin')}>
                {rows.map((row) => (
                    <Row key={row.key} type='button' role='option' aria-selected={view === row.key} $selected={view === row.key} onClick={() => open(row.key)}>
                        {row.icon}
                        <RowName>{row.label}</RowName>
                        <RowGo aria-hidden='true'>
                            <ChevronRightIcon />
                        </RowGo>
                    </Row>
                ))}
            </List>
        </Panel>
    );
};

export default LookEditor;
